// The parent PIN pad (mockup o02): six dots and big keys that work with gloves.
// Submits by itself on the sixth digit; the caller returns an error line or null.
import { useState, type ReactNode } from 'react'
import { PIN_LENGTH } from '../../shared/family'
import { buzz } from '../audio/haptics'
import { pinTick, pinWrong } from '../audio/synth'
import { useMotionTier } from '../motion/tier'
import { t } from '../strings/en'

interface Props {
  title: string
  sub: string
  /** Shown above the title (the person's tile). */
  head?: ReactNode
  /** A message carried over from a previous pad (e.g. "Those didn't match"). */
  note?: string | null
  onSubmit(pin: string): Promise<string | null>
  onCancel?(): void
}

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', '⌫'] as const

export function PinPad({ title, sub, head, note, onSubmit, onCancel }: Props) {
  const [pin, setPin] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  // A wrong PIN shakes the dots (Calm/Still: a red flash); bumped so it replays every time.
  const [wrong, setWrong] = useState(0)
  const tier = useMotionTier()

  const press = async (k: string) => {
    if (busy) return
    pinTick()
    if (k === '⌫') return setPin((p) => p.slice(0, -1))
    const next = (pin + k).slice(0, PIN_LENGTH)
    setPin(next)
    setError(null)
    if (next.length < PIN_LENGTH) return
    setBusy(true)
    const err = await onSubmit(next).catch((e: unknown) => String(e))
    setBusy(false)
    if (err) {
      setError(err)
      setPin('')
      setWrong((n) => n + 1)
      pinWrong()
      buzz('wrong')
    }
  }

  return (
    <div className="pin-pad">
      {onCancel ? (
        <button className="x-btn press pin-back" aria-label={t.pin.back} onClick={onCancel}>
          ←
        </button>
      ) : null}
      {head}
      <h1 className="h1">{title}</h1>
      <p className="muted">{sub}</p>
      <div key={wrong} className={`pin-dots${wrong ? (tier === 'full' ? ' shake' : ' flash') : ''}`} role="status" aria-label={t.pin.entered(pin.length)}>
        {Array.from({ length: PIN_LENGTH }, (_, i) => (
          <i key={i} className={i < pin.length ? 'on' : ''} />
        ))}
      </div>
      <p className="who-error" role="alert">
        {error ?? (pin ? '' : (note ?? ''))}
      </p>
      <div className="pin-keys">
        {KEYS.map((k, i) =>
          k ? (
            <button key={i} className="btn w press" aria-label={k === '⌫' ? t.pin.delete : k} disabled={busy} onClick={() => void press(k)}>
              {k}
            </button>
          ) : (
            <span key={i} />
          ),
        )}
      </div>
    </div>
  )
}
